import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { api, IMPERSONATION_KEY, IMPERSONATION_META_KEY } from "../lib/api";

/**
 * Public 48h marketing preview for Felecia Monroe's CRM.
 *
 * Flow:
 *   1. Fetch a preview JWT from POST /preview/felecia-demo/token.
 *   2. Stash it under IMPERSONATION_KEY so the shared api interceptor
 *      picks it up on every subsequent request without any special-case
 *      code path. Stash a meta object under IMPERSONATION_META_KEY that
 *      the Shell banner keys on to render "PUBLIC PREVIEW" instead of
 *      the amber admin-impersonation strip.
 *   3. Redirect to `/` — the full Run My Day / Leads / Clients app
 *      renders as Felecia, but every mutating request is 403'd by the
 *      backend read-only middleware (`preview_readonly_guard`).
 *
 * When the 48h global window closes, the token endpoint returns 410
 * Gone and we render an "ended" screen instead.
 */
export default function PreviewFelecia() {
  const nav = useNavigate();
  const [status, setStatus] = useState({ state: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If we already have a preview token in this tab, just redirect —
      // don't consume another one from the endpoint.
      try {
        const existing = sessionStorage.getItem(IMPERSONATION_KEY);
        if (existing && existing.startsWith("iKP.")) {
          nav("/", { replace: true });
          return;
        }
      } catch (e) { /* sessionStorage disabled — try minting anyway */ }

      try {
        const { data } = await api.post("/preview/felecia-demo/token");
        if (cancelled) return;
        sessionStorage.setItem(IMPERSONATION_KEY, data.token);
        sessionStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify({
          mode: "public_preview",
          expires_at: data.expires_at,
          window_closes_at: data.window_closes_at,
          seconds_remaining: data.seconds_remaining,
          target: { full_name: data.agent.full_name, brokerage: data.agent.brokerage },
        }));
        nav("/", { replace: true });
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 410) {
          setStatus({
            state: "ended",
            closedAt: detail?.closed_at,
            message: detail?.message,
          });
        } else {
          setStatus({ state: "error", message: detail?.message || detail || "Could not open the preview" });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [nav]);

  if (status.state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-center max-w-md px-6" data-testid="preview-loading">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest">
            <Sparkles className="h-3.5 w-3.5" /> Public preview
          </div>
          <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight text-slate-900">
            Opening Felecia's CRM…
          </h1>
          <p className="mt-2 text-sm text-slate-600 flex items-center justify-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Provisioning your read-only session
          </p>
        </div>
      </div>
    );
  }

  if (status.state === "ended") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-center max-w-md px-6" data-testid="preview-ended">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-200 text-slate-700 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest">
            Preview closed
          </div>
          <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight text-slate-900">
            This preview has ended.
          </h1>
          <p className="mt-3 text-sm text-slate-600">
            The 48-hour public window closed{status.closedAt ? ` on ${new Date(status.closedAt).toLocaleString()}` : ""}. Ping Felecia for a fresh invite.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-center max-w-md px-6" data-testid="preview-error">
        <div className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-800 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest">
          <AlertTriangle className="h-3.5 w-3.5" /> Preview unavailable
        </div>
        <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight text-slate-900">
          Something's off.
        </h1>
        <p className="mt-3 text-sm text-slate-600">{status.message}</p>
      </div>
    </div>
  );
}
